/**
 * Resolução dos links que vêm dentro do HTML gerado pelo backend.
 *
 * O HTML de debug é renderizado num iframe sandboxed (origem opaca), então ele não consegue
 * resolver URLs relativas sozinho nem navegar por conta própria — quem interpreta o `href` é o
 * front, traduzindo-o para uma rota `/painel/debug/...` do React Router. O backend escreve links
 * como se estivesse servindo `/admin/debug/` na raiz (`/ota/queue`, `../fila`, `?estado=pendente`).
 */

export const DEBUG_BASE_PATH = '/painel/debug';

export type DebugNavigation =
  | { kind: 'internal', pagePath: string, query: string }
  | { kind: 'external', url: string }
  | { kind: 'ignore' };

const ABSOLUTE_URL_REGEX = /^[a-z][a-z0-9+.-]*:/i;

/** Aplica `.` e `..` e descarta segmentos vazios, como faria a resolução de URL do browser. */
function normalizeSegments(segments: string[]): string {
  const resolved: string[] = [];
  for (const segment of segments) {
    if (segment === '' || segment === '.') continue;
    if (segment === '..') { resolved.pop(); continue; }
    resolved.push(segment);
  }
  return resolved.join('/');
}

export function resolveDebugHref(currentPagePath: string, rawHref: string|null): DebugNavigation {
  const href = (rawHref ?? '').trim();

  // Âncoras internas da própria página são tratadas pelo browser dentro do iframe.
  if (href === '' || href.startsWith('#')) return { kind: 'ignore' };

  // Protocol-relative (`//host/x`): sempre externo.
  if (href.startsWith('//')) return { kind: 'external', url: `https:${href}` };

  if (ABSOLUTE_URL_REGEX.test(href)) {
    const isHttp = /^https?:/i.test(href);
    // `javascript:`, `mailto:`, `data:` etc. não navegam a página de debug.
    return isHttp ? { kind: 'external', url: href } : { kind: 'ignore' };
  }

  const [pathAndQuery] = href.split('#');
  const queryStart = pathAndQuery.indexOf('?');
  const path = queryStart === -1 ? pathAndQuery : pathAndQuery.slice(0, queryStart);
  const query = queryStart === -1 ? '' : pathAndQuery.slice(queryStart);

  // Só query (`?estado=pendente`) ou só hash: permanece na página atual.
  if (path === '') return { kind: 'internal', pagePath: currentPagePath, query };

  if (path.startsWith('/')) {
    return { kind: 'internal', pagePath: normalizeSegments(path.split('/')), query };
  }

  const currentSegments = currentPagePath.split('/').filter((segment) => segment !== '');
  // Link relativo resolve a partir do "diretório" da página atual, não da página em si.
  const baseSegments = currentSegments.slice(0, -1);
  return { kind: 'internal', pagePath: normalizeSegments([...baseSegments, ...path.split('/')]), query };
}

/** Monta a URL do React Router correspondente a uma navegação interna de debug. */
export function buildDebugRoute(pagePath: string, query: string): string {
  return `${DEBUG_BASE_PATH}${pagePath ? `/${pagePath}` : ''}${query}`;
}
